<template>
  <nav ref="navbar" class="qp-nav" :class="{ 'qp-nav--scrolled': isScrolled }">
    <div class="qp-nav__inner">
      <!-- Wordmark -->
      <button class="qp-nav__brand" type="button" @click="navigateHome" aria-label="QuickPrice">
        <img
          class="qp-nav__brand-mark"
          src="../../assets/quickprice-indigo-icon.svg"
          alt=""
          aria-hidden="true"
        />
        <span class="qp-nav__brand-name">QuickPrice</span>
      </button>

      <!-- Primary links (desktop) -->
      <div class="qp-nav__links">
        <router-link to="/" class="qp-nav__link" exact-active-class="qp-nav__link--active">
          {{ $t('navbar.home') }}
        </router-link>

        <router-link
          v-if="authState.isAuthenticated"
          to="/smart_search"
          class="qp-nav__link"
          active-class="qp-nav__link--active"
        >
          {{ $t('navbar.product_info') }}
        </router-link>

        <div v-if="authState.isAuthenticated" ref="moreDropdown" class="qp-nav__more">
          <button
            type="button"
            class="qp-nav__link qp-nav__link--trigger"
            :class="{ 'qp-nav__link--active': isMoreDropdownOpen }"
            :aria-expanded="isMoreDropdownOpen"
            aria-haspopup="menu"
            @click="toggleMoreDropdown"
          >
            <span>{{ $t('navbar.section_data_tools') }}</span>
            <svg class="qp-nav__chevron" :class="{ 'qp-nav__chevron--open': isMoreDropdownOpen }" width="10" height="6" viewBox="0 0 10 6" aria-hidden="true">
              <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div v-show="isMoreDropdownOpen" class="qp-menu qp-menu--left" role="menu">
            <router-link to="/enstru-search" class="qp-menu__item" role="menuitem">
              <span class="qp-menu__label">{{ $t('navbar.enstru_search') }}</span>
              <span class="qp-menu__hint">ЕНС-ТРУ</span>
            </router-link>
            <router-link v-if="accessState.can('nomenclature.view')" to="/my-nomenclature" class="qp-menu__item" role="menuitem">
              <span class="qp-menu__label">{{ $t('navbar.my_nomenclature') }}</span>
            </router-link>
            <!-- <router-link to="/bulk-ts-generation" class="qp-menu__item" role="menuitem">
              <span class="qp-menu__label">{{ $t('navbar.bulk_ts_generation') }}</span>
              <span class="qp-menu__hint">AI</span>
            </router-link>
            <router-link to="/ai-specification-agent" class="qp-menu__item" role="menuitem">
              <span class="qp-menu__label">{{ $t('navbar.ai_specification_agent') }}</span>
              <span class="qp-menu__hint">PDF</span>
            </router-link> -->
            <router-link to="/suppliers" class="qp-menu__item" role="menuitem">
              <span class="qp-menu__label">{{ $t('navbar.suppliers_directory') || 'База поставщиков' }}</span>
            </router-link>
            <router-link to="/unreliable-suppliers" class="qp-menu__item" role="menuitem">
              <span class="qp-menu__label">{{ $t('navbar.unreliable_suppliers') || 'Реестр недобросовестных поставщиков' }}</span>
              <span class="qp-menu__hint">РНУ</span>
            </router-link>
            <router-link
              v-permission="'domestic_producer_view'"
              to="/domestic-producers"
              class="qp-menu__item"
              role="menuitem"
            >
              <span class="qp-menu__label">{{ $t('navbar.domestic_producers') }}</span>
            </router-link>
          </div>
        </div>

        <router-link to="/about" class="qp-nav__link" active-class="qp-nav__link--active">
          {{ $t('navbar.about') }}
        </router-link>

        <router-link to="/pricing" class="qp-nav__link" active-class="qp-nav__link--active">
          {{ $t('navbar.pricing') }}
        </router-link>

      </div>

      <!-- Right rail -->
      <div class="qp-nav__rail">
        <div v-if="authState.isAuthenticated && organizations.length > 1" class="qp-nav__org">
          <select
            v-model="currentOrganizationId"
            class="qp-nav__org-select"
            :disabled="isSwitchingOrg"
            @change="switchOrganization"
          >
            <option v-for="org in organizations" :key="org.id" :value="org.id">{{ org.name }}</option>
          </select>
        </div>

        <div ref="languageSwitcher" class="qp-nav__lang">
          <button
            type="button"
            class="qp-nav__icon-btn"
            :class="{ 'qp-nav__icon-btn--active': isLanguageDropdownOpen }"
            :aria-expanded="isLanguageDropdownOpen"
            aria-haspopup="menu"
            @click="toggleLanguageDropdown"
          >
            <span class="qp-nav__lang-code">{{ languageDisplay }}</span>
          </button>
          <div v-show="isLanguageDropdownOpen" class="qp-menu qp-menu--right" role="menu">
            <button
              v-for="opt in languageOptions"
              :key="opt.code"
              type="button"
              class="qp-menu__item"
              :class="{ 'qp-menu__item--active': currentLanguage === opt.code }"
              role="menuitem"
              @click="setLanguage(opt.code)"
            >
              <span class="qp-menu__label">{{ opt.label }}</span>
              <span class="qp-menu__hint">{{ opt.code.toUpperCase() }}</span>
            </button>
          </div>
        </div>

        <NotificationsMenu v-if="authState.isAuthenticated" />

        <ThemeToggle />

        <template v-if="authState.isAuthenticated">
          <div ref="profileDropdown" class="qp-nav__profile">
            <button
              type="button"
              class="qp-nav__profile-btn"
              :class="{ 'qp-nav__profile-btn--active': isProfileDropdownOpen }"
              :aria-expanded="isProfileDropdownOpen"
              aria-haspopup="menu"
              @click="toggleProfileDropdown"
            >
              <span class="qp-nav__initials" aria-hidden="true">{{ userInitials }}</span>
            </button>

            <div v-show="isProfileDropdownOpen" class="qp-menu qp-menu--right qp-menu--wide" role="menu" @click="onProfileDropdownClick">
              <header class="qp-menu__header">
                <div class="qp-menu__header-primary">
                  <span class="qp-menu__header-name">{{ userInfo?.username || userInfo?.email || '—' }}</span>
                  <span class="qp-menu__header-role">{{ userInfo?.roleName || authState.role }}</span>
                </div>
                <div
                  v-if="userInfo?.subscription_plan && userInfo.subscription_plan !== 'basic'"
                  class="qp-menu__plan"
                >
                  <span class="qp-menu__plan-label">{{ getSubscriptionLabel(userInfo.subscription_plan) }}</span>
                  <span v-if="userInfo?.subscription_end_date" class="qp-menu__plan-date numeric">
                    {{ $t('user_profile.until') }} {{ formatDate(userInfo.subscription_end_date) }}
                  </span>
                </div>
              </header>

              <TokenCounter ref="tokenCounter" />

              <div class="qp-menu__section">
                <router-link to="/profile" class="qp-menu__item" role="menuitem">
                  <span class="qp-menu__label">{{ $t('navbar.my_profile') }}</span>
                </router-link>
                <router-link to="/research-analytics" class="qp-menu__item" role="menuitem">
                  <span class="qp-menu__label">{{ $t('navbar.research_analytics') }}</span>
                </router-link>
              </div>

              <template v-if="userInfo?.role === 'admin'">
                <div class="qp-menu__divider" aria-hidden="true"></div>
                <div class="qp-menu__section">
                  <span class="qp-menu__section-label">{{ $t('navbar.section_admin') }}</span>
                  <router-link to="/saved-names" class="qp-menu__item" role="menuitem">
                    <span class="qp-menu__label">{{ $t('navbar.saved_names') }}</span>
                  </router-link>
                  <router-link to="/manage-users" class="qp-menu__item" role="menuitem">
                    <span class="qp-menu__label">{{ $t('navbar.manage_users') }}</span>
                  </router-link>
                  <router-link to="/user-tags" class="qp-menu__item" role="menuitem">
                    <span class="qp-menu__label">{{ $t('navbar.user_tags') }}</span>
                  </router-link>
                  <router-link to="/admin/organizations" class="qp-menu__item" role="menuitem">
                    <span class="qp-menu__label">{{ $t('navbar.organizations') }}</span>
                  </router-link>
                  <router-link to="/admin/abc-catalog" class="qp-menu__item" role="menuitem">
                    <span class="qp-menu__label">{{ $t('navbar.abc_catalog') }}</span>
                  </router-link>
                  <router-link to="/admin/system-directories" class="qp-menu__item" role="menuitem">
                    <span class="qp-menu__label">Системные справочники</span>
                  </router-link>
                  <router-link to="/admin/apikeys" class="qp-menu__item" role="menuitem">
                    <span class="qp-menu__label">{{ $t('navbar.api_keys') }}</span>
                  </router-link>
                  <router-link to="/parser-settings" class="qp-menu__item" role="menuitem">
                    <span class="qp-menu__label">{{ $t('navbar.parser_settings') }}</span>
                  </router-link>
                </div>
              </template>

              <div class="qp-menu__divider" aria-hidden="true"></div>
              <button type="button" class="qp-menu__item qp-menu__item--danger" @click="handleLogout">
                <span class="qp-menu__label">{{ $t('navbar.logout') }}</span>
              </button>
            </div>
          </div>
        </template>
        <template v-else>
          <button type="button" class="qp-nav__login" @click="handleLogin">
            {{ $t('navbar.login') }}
          </button>
        </template>
      </div>

      <!-- Mobile trigger -->
      <!-- <button
        type="button"
        class="qp-nav__burger"
        :class="{ 'qp-nav__burger--open': isDropdownOpen }"
        :aria-expanded="isDropdownOpen"
        aria-label="Menu"
        @click="toggleDropdown"
      >
        <span></span><span></span><span></span>
      </button> -->
    </div>

    <!-- Mobile drawer -->
    <div
      v-show="isDropdownOpen"
      class="qp-nav__drawer"
      :class="{ 'qp-nav__drawer--open': isDropdownOpen }"
      @click="closeDropdown"
    >
      <div class="qp-nav__drawer-inner">
        <div class="qp-nav__drawer-section">
          <span class="qp-eyebrow">{{ $t('navbar.section_info') }}</span>
          <router-link to="/about" class="qp-nav__drawer-link nav-link">{{ $t('navbar.about') }}</router-link>
          <router-link to="/pricing" class="qp-nav__drawer-link nav-link">{{ $t('navbar.pricing') }}</router-link>
        </div>

        <div v-if="!authState.isAuthenticated" class="qp-nav__drawer-section">
          <span class="qp-eyebrow">{{ $t('navbar.section_account') }}</span>
          <button type="button" class="qp-nav__drawer-link nav-link" @click="handleLogin">{{ $t('navbar.login') }}</button>
        </div>

        <div v-if="authState.isAuthenticated" class="qp-nav__drawer-section">
          <span class="qp-eyebrow">{{ $t('navbar.section_main_features') }}</span>
          <router-link to="/smart_search" class="qp-nav__drawer-link nav-link">{{ $t('navbar.product_info') }}</router-link>
          <router-link to="/my_market_research" class="qp-nav__drawer-link nav-link">{{ $t('navbar.my_market_research') }}</router-link>
        </div>

        <div v-if="authState.isAuthenticated" class="qp-nav__drawer-section">
          <span class="qp-eyebrow">{{ $t('navbar.section_data_tools') }}</span>
          <router-link to="/enstru-search" class="qp-nav__drawer-link nav-link">{{ $t('navbar.enstru_search') }}</router-link>
          <router-link v-if="accessState.can('nomenclature.view')" to="/my-nomenclature" class="qp-nav__drawer-link nav-link">{{ $t('navbar.my_nomenclature') }}</router-link>
          <!-- <router-link to="/bulk-ts-generation" class="qp-nav__drawer-link nav-link">{{ $t('navbar.bulk_ts_generation') }}</router-link> -->
          <router-link to="/ai-specification-agent" class="qp-nav__drawer-link nav-link">{{ $t('navbar.ai_specification_agent') }}</router-link>
          <router-link to="/suppliers" class="qp-nav__drawer-link nav-link">{{ $t('navbar.suppliers_directory') || 'База поставщиков' }}</router-link>
          <router-link to="/unreliable-suppliers" class="qp-nav__drawer-link nav-link">{{ $t('navbar.unreliable_suppliers') || 'Реестр недобросовестных поставщиков' }}</router-link>
          <router-link
            v-permission="'domestic_producer_view'"
            to="/domestic-producers"
            class="qp-nav__drawer-link nav-link"
          >{{ $t('navbar.domestic_producers') }}</router-link>
        </div>

        <div v-if="authState.isAuthenticated && userInfo?.role === 'admin'" class="qp-nav__drawer-section">
          <span class="qp-eyebrow">{{ $t('navbar.section_admin') }}</span>
          <router-link to="/saved-names" class="qp-nav__drawer-link nav-link">{{ $t('navbar.saved_names') }}</router-link>
          <router-link to="/manage-users" class="qp-nav__drawer-link nav-link">{{ $t('navbar.manage_users') }}</router-link>
          <router-link to="/user-tags" class="qp-nav__drawer-link nav-link">{{ $t('navbar.user_tags') }}</router-link>
          <router-link to="/admin/organizations" class="qp-nav__drawer-link nav-link">{{ $t('navbar.organizations') }}</router-link>
          <router-link to="/admin/apikeys" class="qp-nav__drawer-link nav-link">{{ $t('navbar.api_keys') }}</router-link>
          <router-link to="/admin/system-directories" class="qp-nav__drawer-link nav-link">Системные справочники</router-link>
          <router-link to="/parser-settings" class="qp-nav__drawer-link nav-link">{{ $t('navbar.parser_settings') }}</router-link>
          <router-link to="/redirect-urls" class="qp-nav__drawer-link nav-link">{{ $t('navbar.redirect_urls') }}</router-link>
        </div>

        <div v-if="authState.isAuthenticated" class="qp-nav__drawer-section">
          <router-link to="/profile" class="qp-nav__drawer-link nav-link">{{ $t('navbar.my_profile') }}</router-link>
          <router-link to="/research-analytics" class="qp-nav__drawer-link nav-link">{{ $t('navbar.research_analytics') }}</router-link>
        </div>

        <div class="qp-nav__drawer-section">
          <span class="qp-eyebrow">{{ $t('navbar.language_switch') }}</span>
          <div class="qp-nav__drawer-langs">
            <button
              v-for="opt in languageOptions"
              :key="opt.code"
              type="button"
              class="qp-nav__drawer-lang nav-link"
              :class="{ 'qp-nav__drawer-lang--active': currentLanguage === opt.code }"
              @click="setLanguage(opt.code)"
            >
              <span>{{ opt.label }}</span>
              <span class="qp-nav__drawer-lang-code">{{ opt.code.toUpperCase() }}</span>
            </button>
          </div>
          <div class="qp-nav__drawer-theme">
            <span>{{ $t('theme.title') }}</span>
            <ThemeToggle />
          </div>
        </div>

        <div v-if="authState.isAuthenticated" class="qp-nav__drawer-section">
          <button type="button" class="qp-nav__drawer-link logout-button" @click="handleLogout">
            {{ $t('navbar.logout') }}
          </button>
        </div>
      </div>
    </div>
  </nav>
  <NavbarTour :show="showNavbarTour" @close="onTourClosed" />
</template>

<script>
import { authState, logout, updateRole } from '@/store/auth';
import { useRouter } from 'vue-router';
import { ref, onMounted, onBeforeUnmount, computed, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { vPermission } from '@/directives/permission';
import ThemeToggle from './ThemeToggle.vue';
import NavbarTour from '@/components/onboarding/NavbarTour.vue';
import TokenCounter from './TokenCounter.vue';
import NotificationsMenu from './NotificationsMenu.vue';
import { shouldShowNavbarTour } from '@/composables/useOnboarding';
import { isTokenValid } from '@/utils/tokenUtils';
import userService from '@/services/userService';
import { accessState } from '@/store/access';

const LANGUAGE_OPTIONS = [
  { code: 'ru', label: 'Русский' },
  { code: 'en', label: 'English' },
  { code: 'kk', label: 'Қазақша' },
  { code: 'zh', label: '中文' },
];

export default {
  name: 'NavBar',
  components: { ThemeToggle, NavbarTour, TokenCounter, NotificationsMenu },
  directives: { permission: vPermission },
  setup() {
    const router = useRouter();
    const { locale, t } = useI18n();

    const navbar = ref(null);
    const languageSwitcher = ref(null);
    const moreDropdown = ref(null);
    const profileDropdown = ref(null);
    const tokenCounter = ref(null);

    const isDropdownOpen = ref(false);
    const isLanguageDropdownOpen = ref(false);
    const isMoreDropdownOpen = ref(false);
    const isProfileDropdownOpen = ref(false);
    const isScrolled = ref(false);

    const userInfo = ref(null);
    const organizations = ref([]);
    const currentOrganizationId = ref(null);
    const isSwitchingOrg = ref(false);
    const showNavbarTour = ref(false);

    const currentLanguage = computed(() => locale.value);
    const languageDisplay = computed(() => (currentLanguage.value || 'en').toUpperCase().slice(0, 2));

    const userInitials = computed(() => {
      const src = userInfo.value?.username || userInfo.value?.email || '';
      const parts = src.split(/[\s@._-]+/).filter(Boolean).slice(0, 2);
      const letters = parts.map(p => p.charAt(0).toUpperCase()).join('');
      return letters || '·';
    });

    const closeAllDropdowns = () => {
      isLanguageDropdownOpen.value = false;
      isMoreDropdownOpen.value = false;
      isProfileDropdownOpen.value = false;
    };

    const toggleLanguageDropdown = () => {
      const next = !isLanguageDropdownOpen.value;
      closeAllDropdowns();
      isLanguageDropdownOpen.value = next;
    };

    const toggleMoreDropdown = () => {
      const next = !isMoreDropdownOpen.value;
      closeAllDropdowns();
      isMoreDropdownOpen.value = next;
    };

    const toggleProfileDropdown = () => {
      const next = !isProfileDropdownOpen.value;
      closeAllDropdowns();
      isProfileDropdownOpen.value = next;
    };

    const setLanguage = (lang) => {
      locale.value = lang;
      document.documentElement.lang = lang;
      localStorage.setItem('user-language', lang);
      isDropdownOpen.value = false;
      isLanguageDropdownOpen.value = false;
    };

    const getSubscriptionLabel = (plan) => t(`user_profile.plans.${plan}`);

    const formatDate = (dateString) => {
      if (!dateString) return '';
      const localeMap = { ru: 'ru-RU', en: 'en-US', kk: 'kk-KZ', zh: 'zh-CN' };
      const browserLocale = localeMap[locale.value] || 'ru-RU';
      return new Date(dateString).toLocaleDateString(browserLocale, {
        day: '2-digit', month: '2-digit', year: 'numeric',
      });
    };

    const toggleDropdown = () => { isDropdownOpen.value = !isDropdownOpen.value; };

    const closeDropdown = (event) => {
      if (event?.target?.closest('.nav-link, .logout-button')) {
        isDropdownOpen.value = false;
      }
    };

    const onProfileDropdownClick = (event) => {
      if (event?.target?.closest('.qp-menu__item')) {
        isProfileDropdownOpen.value = false;
      }
    };

    const handleClickOutside = (event) => {
      if (isDropdownOpen.value && navbar.value && !navbar.value.contains(event.target)) {
        isDropdownOpen.value = false;
      }
      if (isLanguageDropdownOpen.value && languageSwitcher.value && !languageSwitcher.value.contains(event.target)) {
        isLanguageDropdownOpen.value = false;
      }
      if (isMoreDropdownOpen.value && moreDropdown.value && !moreDropdown.value.contains(event.target)) {
        isMoreDropdownOpen.value = false;
      }
      if (isProfileDropdownOpen.value && profileDropdown.value && !profileDropdown.value.contains(event.target)) {
        isProfileDropdownOpen.value = false;
      }
    };

    const onScroll = () => {
      isScrolled.value = window.scrollY > 4;
    };

    const navigateHome = () => {
      router.push('/');
      isDropdownOpen.value = false;
    };

    const handleLogin = () => {
      isDropdownOpen.value = false;
      router.push('/login');
    };

    const handleLogout = async () => {
      try {
        isDropdownOpen.value = false;
        isProfileDropdownOpen.value = false;
        const { default: api } = await import('@/services/axios');
        await api.post('/auth/logout');
        logout();
        window.$toast?.show(t('auth.loggedOut'), 'success');
        window.location.replace('/');
      } catch (e) {
        try { logout(); } catch { /* noop */ }
        window.location.replace('/');
      }
    };

    const onTourClosed = () => { showNavbarTour.value = false; };

    const loadUserFromServer = async () => {
      if (!authState.isAuthenticated || !isTokenValid()) {
        userInfo.value = null;
        return;
      }
      try {
        const serverData = await userService.getUserInfo();
        userInfo.value = {
          username: serverData.username,
          email: serverData.email || serverData.username,
          role: serverData.role || 'user',
          roleName: serverData.roleName || 'Неизвестная роль',
          subscription_plan: serverData.subscription_plan,
          subscription_end_date: serverData.subscription_end_date,
          remaining_requests: serverData.remaining_requests,
          daily_limit: serverData.daily_limit,
        };
        if (serverData.role) updateRole(serverData.role);
        if (Array.isArray(serverData.permissions)) {
          accessState.setContext({
            permissions: serverData.permissions,
            organization_id: serverData.organization_id,
          });
        }
      } catch (error) {
        console.error('Ошибка при загрузке данных пользователя с сервера:', error);
        userInfo.value = null;
      }
    };

    const loadOrganizations = async () => {
      if (!authState.isAuthenticated || !isTokenValid()) {
        organizations.value = [];
        currentOrganizationId.value = null;
        return;
      }
      try {
        const { default: api } = await import('@/services/axios');
        const res = await api.get('/organizations/my');
        organizations.value = res.data || [];
        const current = organizations.value.find((org) => org.is_current);
        currentOrganizationId.value = current ? current.id : (organizations.value[0]?.id || null);
      } catch (e) {
        organizations.value = [];
      }
    };

    const switchOrganization = async () => {
      if (!currentOrganizationId.value) return;
      isSwitchingOrg.value = true;
      try {
        const { default: api } = await import('@/services/axios');
        await api.post('/organizations/switch', { organization_id: Number(currentOrganizationId.value) });
        await loadOrganizations();
        await accessState.load(true);
        window.dispatchEvent(new CustomEvent('organization-changed'));
      } catch (e) {
        window.$toast?.show(t('common.error_occurred', 'Произошла ошибка'), 'error');
      } finally {
        isSwitchingOrg.value = false;
      }
    };

    const handleOrganizationChanged = async () => {
      await loadOrganizations();
      await loadUserFromServer();
    };

    onMounted(async () => {
      const savedLang = localStorage.getItem('user-language');
      if (savedLang && locale.value !== savedLang) locale.value = savedLang;
      document.documentElement.lang = locale.value;

      await loadUserFromServer();
      await loadOrganizations();

      await nextTick();
      document.addEventListener('click', handleClickOutside);
      window.addEventListener('organization-changed', handleOrganizationChanged);
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();

      showNavbarTour.value = shouldShowNavbarTour();
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('organization-changed', handleOrganizationChanged);
      window.removeEventListener('scroll', onScroll);
    });

    watch(
      () => authState.isAuthenticated,
      async (isAuth, wasAuth) => {
        if (isAuth && !wasAuth) {
          await nextTick();
          showNavbarTour.value = shouldShowNavbarTour();
          await loadUserFromServer();
          await loadOrganizations();
        } else if (!isAuth && wasAuth) {
          userInfo.value = null;
          organizations.value = [];
          currentOrganizationId.value = null;
        }
      }
    );

    return {
      authState,
      accessState,
      userInfo,
      organizations,
      currentOrganizationId,
      isSwitchingOrg,
      navbar,
      languageSwitcher,
      moreDropdown,
      profileDropdown,
      tokenCounter,
      isDropdownOpen,
      isLanguageDropdownOpen,
      isMoreDropdownOpen,
      isProfileDropdownOpen,
      isScrolled,
      currentLanguage,
      languageDisplay,
      languageOptions: LANGUAGE_OPTIONS,
      userInitials,
      toggleDropdown,
      closeDropdown,
      navigateHome,
      handleLogin,
      handleLogout,
      toggleLanguageDropdown,
      toggleMoreDropdown,
      toggleProfileDropdown,
      setLanguage,
      getSubscriptionLabel,
      formatDate,
      showNavbarTour,
      onTourClosed,
      onProfileDropdownClick,
      loadUserFromServer,
      switchOrganization,
    };
  },
};
</script>

<style scoped>
/* ============================================================
   Shell — sticky, thin, hairline bottom
   ============================================================ */

.qp-nav {
  position: fixed;
  inset: 0 0 auto 0;
  z-index: var(--z-nav);
  height: var(--layout-navbar-h);
  background: var(--surface-base);
  border-bottom: 1px solid var(--hairline);
  transition: border-color var(--dur-sm) var(--ease-out-quart),
              background-color var(--dur-sm) var(--ease-out-quart);
  animation: qp-nav-slide 520ms var(--ease-out-expo) both;
}

@keyframes qp-nav-slide {
  from { opacity: 0; transform: translateY(-12px); }
  to { opacity: 1; transform: translateY(0); }
}

.qp-nav--scrolled {
  border-bottom-color: var(--hairline-strong);
}

.qp-nav__inner {
  display: flex;
  align-items: center;
  gap: var(--space-lg);
  height: 100%;
  max-width: var(--layout-shell-max);
  margin-inline: auto;
  padding-inline: var(--space-lg);
}

/* ============================================================
   Brand wordmark
   ============================================================ */

.qp-nav__brand {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 0;
  background: none;
  border: 0;
  cursor: pointer;
  color: var(--ink);
  line-height: 1;
}

.qp-nav__brand-mark {
  flex: 0 0 auto;
  display: block;
  width: 22px;
  height: 22px;
  object-fit: contain;
  transition: transform var(--dur-md) var(--ease-out-expo);
  animation: qp-brand-pulse 620ms var(--ease-out-expo) 200ms both;
}

.qp-nav__brand:hover .qp-nav__brand-mark {
  transform: scale(1.08);
}

.qp-nav__brand:active .qp-nav__brand-mark {
  transform: scale(0.94);
}

@keyframes qp-brand-pulse {
  0%   { opacity: 0; transform: scale(0.6); }
  60%  { opacity: 1; transform: scale(1.12); }
  100% { opacity: 1; transform: scale(1); }
}

.qp-nav__brand-name {
  font-size: var(--text-md);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-tight);
  color: var(--ink);
}

/* ============================================================
   Primary links — text with accent underline on active
   ============================================================ */

.qp-nav__links {
  display: flex;
  align-items: center;
  gap: var(--space-md);
  height: 100%;
  margin-inline-start: var(--space-md);
}

.qp-nav__link {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 100%;
  padding-inline: 2px;
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  line-height: 1;
  color: var(--ink-muted);
  background: none;
  border: 0;
  cursor: pointer;
  transition: color var(--dur-xs) var(--ease-out-quart);
  animation: qp-link-in 380ms var(--ease-out-expo) both;
}

.qp-nav__links > :nth-child(1) { animation-delay: 120ms; }
.qp-nav__links > :nth-child(2) { animation-delay: 180ms; }
.qp-nav__links > :nth-child(3) { animation-delay: 240ms; }
.qp-nav__more > .qp-nav__link--trigger { animation-delay: inherit; }

@keyframes qp-link-in {
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
}

.qp-nav__link::after {
  content: '';
  position: absolute;
  inset: auto 0 -1px 0;
  height: 2px;
  background: var(--accent);
  transform: scaleX(0);
  transform-origin: 0% 50%;
  transition: transform var(--dur-md) var(--ease-out-expo);
}

.qp-nav__link:hover {
  color: var(--ink);
}

.qp-nav__link:hover::after {
  transform: scaleX(1);
  transform-origin: 0% 50%;
  opacity: 0.4;
}

.qp-nav__link--active {
  color: var(--ink);
}

.qp-nav__link--active::after {
  transform: scaleX(1);
  transform-origin: 0% 50%;
  opacity: 1;
}

.qp-nav__link--trigger .qp-nav__chevron {
  flex: 0 0 auto;
  transition: transform var(--dur-sm) var(--ease-out-expo);
  opacity: 0.7;
}

.qp-nav__link--trigger .qp-nav__chevron--open {
  transform: rotate(180deg);
  opacity: 1;
}

.qp-nav__more {
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
}

/* ============================================================
   Right rail — org, language, theme, profile
   ============================================================ */

.qp-nav__rail {
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  margin-inline-start: auto;
  height: 100%;
}

.qp-nav__org {
  display: inline-flex;
  align-items: center;
}

.qp-nav__org-select {
  height: 30px;
  padding: 0 28px 0 10px;
  font-family: inherit;
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
  color: var(--ink);
  background: var(--surface-sunken);
  border: 1px solid var(--hairline);
  border-radius: var(--radius-sm);
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path d='M1 1l4 4 4-4' fill='none' stroke='%23888' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  background-repeat: no-repeat;
  background-position: right 10px center;
  max-width: 180px;
  text-overflow: ellipsis;
  transition: border-color var(--dur-xs) var(--ease-out-quart);
}

.qp-nav__org-select:hover { border-color: var(--hairline-strong); }
.qp-nav__org-select:focus-visible {
  outline: 0;
  border-color: var(--accent);
  box-shadow: var(--shadow-focus);
}

.qp-nav__lang {
  position: relative;
}

.qp-nav__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  min-width: 30px;
  padding-inline: 8px;
  color: var(--ink-muted);
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: color var(--dur-xs) var(--ease-out-quart),
              background-color var(--dur-xs) var(--ease-out-quart),
              border-color var(--dur-xs) var(--ease-out-quart);
}

.qp-nav__icon-btn:hover {
  color: var(--ink);
  background: var(--surface-sunken);
}

.qp-nav__icon-btn--active {
  color: var(--ink);
  background: var(--surface-sunken);
  border-color: var(--hairline);
}

.qp-nav__lang-code {
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-caps);
}

.qp-nav__profile {
  position: relative;
  margin-inline-start: var(--space-2xs);
}

.qp-nav__profile-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: var(--surface-sunken);
  color: var(--ink);
  border: 1px solid var(--hairline);
  border-radius: var(--radius-pill);
  cursor: pointer;
  transition:
    border-color var(--dur-xs) var(--ease-out-quart),
    background-color var(--dur-xs) var(--ease-out-quart),
    transform var(--dur-sm) var(--ease-out-expo),
    box-shadow var(--dur-sm) var(--ease-out-quart);
}

.qp-nav__profile-btn:hover,
.qp-nav__profile-btn--active {
  border-color: var(--accent);
  background: var(--accent-tint);
  transform: translateY(-1px);
  box-shadow: 0 3px 10px oklch(50% 0.110 200 / 0.15);
}

.qp-nav__profile-btn:active {
  transform: translateY(0);
}

.qp-nav__initials {
  font-size: var(--text-2xs);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-caps);
  color: var(--ink);
  line-height: 1;
}

.qp-nav__login {
  height: 32px;
  padding-inline: var(--space-md);
  font-family: inherit;
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--accent-contrast);
  background: var(--accent);
  border: 0;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background-color var(--dur-xs) var(--ease-out-quart);
}

.qp-nav__login:hover { background: var(--accent-hover); }

/* ============================================================
   Dropdown menus — shared
   ============================================================ */

.qp-menu {
  position: absolute;
  top: calc(100% + 8px);
  min-width: 200px;
  padding: 4px;
  background: var(--surface-base);
  border: 1px solid var(--hairline-strong);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  z-index: var(--z-dropdown);
  transform-origin: top center;
  animation: qp-menu-in 260ms var(--ease-out-expo);
}

.qp-menu--left { left: 0; transform-origin: top left; }
.qp-menu--right { right: 0; transform-origin: top right; }
.qp-menu--wide { min-width: 280px; padding: 0; }

@keyframes qp-menu-in {
  from { opacity: 0; transform: translateY(-6px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

.qp-menu__item {
  animation: qp-menu-item-in 280ms var(--ease-out-expo) both;
}
.qp-menu__section .qp-menu__item:nth-child(1) { animation-delay: 20ms; }
.qp-menu__section .qp-menu__item:nth-child(2) { animation-delay: 50ms; }
.qp-menu__section .qp-menu__item:nth-child(3) { animation-delay: 80ms; }
.qp-menu__section .qp-menu__item:nth-child(4) { animation-delay: 110ms; }
.qp-menu__section .qp-menu__item:nth-child(5) { animation-delay: 140ms; }
.qp-menu__section .qp-menu__item:nth-child(6) { animation-delay: 170ms; }
.qp-menu__section .qp-menu__item:nth-child(7) { animation-delay: 200ms; }

@keyframes qp-menu-item-in {
  from { opacity: 0; transform: translateX(-4px); }
  to { opacity: 1; transform: translateX(0); }
}

.qp-menu__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-md);
  width: 100%;
  min-height: 36px;
  padding: 8px 10px;
  font-family: inherit;
  font-size: var(--text-sm);
  font-weight: var(--weight-regular);
  line-height: var(--leading-snug);
  text-align: start;
  color: var(--ink);
  background: transparent;
  border: 0;
  border-radius: var(--radius-xs);
  cursor: pointer;
  transition: background-color var(--dur-xs) var(--ease-out-quart),
              color var(--dur-xs) var(--ease-out-quart);
}

.qp-menu__item:hover { background: var(--surface-sunken); }
.qp-menu__item--active { background: var(--accent-tint); color: var(--accent); }
.qp-menu__item--danger { color: var(--danger); }
.qp-menu__item--danger:hover { background: var(--danger-tint); }

.qp-menu__label {
  flex: 1;
  min-width: 0;
  display: inline-flex;
  align-items: center;
}
.qp-menu__hint {
  font-size: var(--text-2xs);
  font-weight: var(--weight-medium);
  color: var(--ink-faint);
  letter-spacing: var(--tracking-caps);
  text-transform: uppercase;
}

.qp-menu__header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: var(--space-sm) var(--space-md);
  border-bottom: 1px solid var(--hairline);
}

.qp-menu__header-primary {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.qp-menu__header-name {
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--ink);
  word-break: break-all;
}

.qp-menu__header-role {
  font-size: var(--text-2xs);
  font-weight: var(--weight-medium);
  color: var(--ink-faint);
  letter-spacing: var(--tracking-caps);
  text-transform: uppercase;
}

.qp-menu__plan {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 8px;
  margin-block-start: 4px;
  background: var(--accent-tint);
  border-radius: var(--radius-xs);
  font-size: var(--text-2xs);
}

.qp-menu__plan-label {
  font-weight: var(--weight-semibold);
  color: var(--accent);
}

.qp-menu__plan-date {
  color: var(--ink-muted);
}

.qp-menu__section {
  display: flex;
  flex-direction: column;
  padding: 4px;
}

.qp-menu__section-label {
  padding: 6px 10px 2px;
  font-size: var(--text-3xs);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-caps);
  text-transform: uppercase;
  color: var(--ink-faint);
}

.qp-menu__divider {
  height: 1px;
  background: var(--hairline);
}

/* ============================================================
   Mobile burger + drawer
   ============================================================ */

.qp-nav__burger {
  display: none;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: transparent;
  border: 0;
  cursor: pointer;
  color: var(--ink);
  border-radius: var(--radius-sm);
  transition: background-color var(--dur-xs) var(--ease-out-quart);
}

.qp-nav__burger:hover { background: var(--surface-sunken); }
.qp-nav__burger:active { transform: scale(0.94); }

.qp-nav__burger span {
  display: block;
  width: 16px;
  height: 1.5px;
  background: currentColor;
  border-radius: 1px;
  position: absolute;
  transition: transform 320ms var(--ease-out-expo),
              opacity var(--dur-xs) var(--ease-out-quart),
              width 320ms var(--ease-out-expo);
}

.qp-nav__burger span:nth-child(1) { transition-delay: 0ms; }
.qp-nav__burger span:nth-child(2) { transition-delay: 40ms; }
.qp-nav__burger span:nth-child(3) { transition-delay: 80ms; }
.qp-nav__burger:hover span:nth-child(2) { width: 12px; }
.qp-nav__burger span:nth-child(1) { transform: translateY(-5px); }
.qp-nav__burger span:nth-child(3) { transform: translateY(5px); }

.qp-nav__burger--open span:nth-child(1) { transform: translateY(0) rotate(45deg); }
.qp-nav__burger--open span:nth-child(2) { opacity: 0; }
.qp-nav__burger--open span:nth-child(3) { transform: translateY(0) rotate(-45deg); }

.qp-nav__drawer {
  position: fixed;
  inset: var(--layout-navbar-h) 0 0 0;
  background: var(--surface-base);
  border-top: 1px solid var(--hairline);
  overflow-y: auto;
  animation: qp-drawer-in var(--dur-md) var(--ease-out-expo);
  z-index: calc(var(--z-nav) - 1);
}

@keyframes qp-drawer-in {
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
}

.qp-nav__drawer-inner {
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
  padding: var(--space-lg);
  max-width: var(--layout-shell-max);
  margin-inline: auto;
}

.qp-nav__drawer-section {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.qp-nav__drawer-section .qp-eyebrow {
  margin-block-end: 4px;
}

.qp-nav__drawer-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-md);
  padding: 12px 0;
  font-size: var(--text-md);
  font-weight: var(--weight-medium);
  color: var(--ink);
  background: transparent;
  border: 0;
  border-bottom: 1px solid var(--hairline);
  text-align: start;
  cursor: pointer;
  transition: color var(--dur-xs) var(--ease-out-quart),
              padding var(--dur-xs) var(--ease-out-quart);
}

.qp-nav__drawer-link:hover { color: var(--accent); padding-inline-start: 4px; }

.qp-nav__drawer-link.logout-button { color: var(--danger); }

.qp-nav__drawer-langs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-2xs);
  margin-block-start: var(--space-xs);
}

.qp-nav__drawer-lang {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--ink);
  background: var(--surface-sunken);
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  cursor: pointer;
  text-align: start;
  transition: border-color var(--dur-xs) var(--ease-out-quart),
              background-color var(--dur-xs) var(--ease-out-quart);
}

.qp-nav__drawer-lang--active {
  background: var(--accent-tint);
  border-color: var(--hairline-accent);
  color: var(--accent);
}

.qp-nav__drawer-lang-code {
  font-size: var(--text-2xs);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-caps);
  color: var(--ink-faint);
}

.qp-nav__drawer-theme {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-block-start: var(--space-md);
  padding: 12px 0;
  font-size: var(--text-sm);
  color: var(--ink);
  border-top: 1px solid var(--hairline);
}

/* ============================================================
   Responsive
   ============================================================ */

@media (max-width: 1080px) {
  .qp-nav__inner { padding-inline: var(--space-md); gap: var(--space-md); }
  .qp-nav__links { gap: var(--space-sm); }
  .qp-nav__org-select { max-width: 140px; }
}

@media (max-width: 768px) {
  .qp-nav__links { display: none; }
  .qp-nav__org { display: none; }
  .qp-nav__burger { display: inline-flex; position: relative; }
  .qp-nav__rail { gap: 4px; }
}

@media (max-width: 560px) {
  .qp-nav { height: var(--layout-navbar-h-mobile); }
  .qp-nav__drawer { inset: var(--layout-navbar-h-mobile) 0 0 0; }
  .qp-nav__inner { padding-inline: var(--space-sm); gap: var(--space-xs); }
  .qp-nav__lang { display: none; }
  .qp-nav__brand-name { font-size: var(--text-sm); }
  .qp-nav__drawer-inner { padding: var(--space-md); gap: var(--space-md); }
}

@media (prefers-reduced-motion: reduce) {
  .qp-nav,
  .qp-nav__brand-mark,
  .qp-nav__link,
  .qp-nav__link::after,
  .qp-nav__chevron,
  .qp-menu,
  .qp-menu__item,
  .qp-nav__drawer { animation: none; transition: none; }
}
</style>
